import { OfferChannel, TriggerEventType } from '@today/types'
import { z } from 'zod'

// ─── Snapshot creation input ──────────────────────────────────────────────────
//
// The in-process input the render pipeline supplies at finalization. `tenantId`,
// `offerId` and `customerId` are DB-resolved identifiers (never request-forged);
// `renderedAt` is optional and defaults to now when omitted. There is NO update
// schema — snapshots are immutable.

export const offerSnapshotCreateSchema = z.object({
	tenantId: z.uuid(),
	offerId: z.uuid(),
	customerId: z.uuid(),
	// TO-388: the org unit of the offer's customer at render time — the record's
	// owner for hierarchy-scoped reads. Required for every new snapshot.
	organizationUnitId: z.uuid(),
	htmlS3Key: z.string().min(1),
	pdfS3Key: z.string().min(1),
	personalizationPayload: z.record(z.string(), z.unknown()),
	renderedAt: z.iso.datetime().optional(),
	// TO-464 — set by the artefact join. The id is minted before the insert so the
	// published event can name the snapshot; the version is the offer's pending
	// render version (omitted only by the legacy path, which assigns max + 1).
	offerSnapshotId: z.uuid().optional(),
	renderVersionNumber: z.number().int().min(1).optional(),
	htmlSha256: z
		.string()
		.regex(/^[0-9a-f]{64}$/)
		.nullable()
		.optional(),
	pdfSha256: z
		.string()
		.regex(/^[0-9a-f]{64}$/)
		.nullable()
		.optional(),
	htmlS3VersionId: z.string().min(1).nullable().optional(),
	pdfS3VersionId: z.string().min(1).nullable().optional(),
	triggerEventType: z.enum(TriggerEventType).nullable().optional(),
	triggerEventId: z.uuid().nullable().optional(),
	triggeredByUserId: z.uuid().nullable().optional(),
	sendChannels: z.array(z.enum(OfferChannel)).nullable().optional(),
})

export type OfferSnapshotCreateInput = z.infer<typeof offerSnapshotCreateSchema>
